import * as React from 'react'

import { NetworkLogo } from 'main/components/interface'
import BookmarkButton, { Style as BookmarkButtonStyle } from 'main/components/lead/BookmarkButton'
import { PhilosophyTag } from 'main/components/profile'
import { Link, spacing, Strong } from 'main/ui'
import { AssetName } from 'main/util/asset'
import { determineImage } from 'main/util/school'
import { UnreachableCaseError } from 'main/util/ts'

import { useWhitelabel } from 'main/framework'
import { ActionLinks, Hours, Photo, Pricing, UpcomingEventBadge, WSVerifiedIcon } from '../common'
import NetworkRating from '../common/NetworkRating'
import NetworkRatingV2 from '../common/NetworkRatingV2'
import RatingDisplay from '../common/RatingDisplay'
import { ILeadStatus, Target, TargetType, UpcomingEvent, UpcomingEventType } from '../types'
import {
  getBookmarkButtonSchoolIdForTarget,
  getDataForTarget,
  getDisplayedUpcomingEvent,
} from '../util'
import {
  BookmarkContainer,
  CalloutLayout,
  InfoContainer,
  NameContainer,
  PhotoContainer,
  SubInfoContainer,
  UpcomingGatheringEventContainer,
  UpcomingTourEventContainer,
} from './Content.styles'

interface IProps {
  target: Target
  upcomingEvent: UpcomingEvent | null
  leadStatus?: ILeadStatus
}

function getIdFor(target: Target) {
  switch (target._type) {
    case TargetType.ExternalSchool:
      return target.externalSchool.id
    case TargetType.School:
      return target.school.id
    case TargetType.SchoolListing:
      return target.listing.schoolId || target.listing.externalSchoolId
  }
}

export default function Content(props: IProps) {
  const { target, leadStatus, upcomingEvent } = props
  const displayedUpcomingEvent = getDisplayedUpcomingEvent(leadStatus || null, upcomingEvent)
  const bookmarkButtonSchoolId = getBookmarkButtonSchoolIdForTarget(target)

  const {
    hours,
    imageUrl,
    isVerified,
    name,
    pagePath,
    philosophy,
    pricing,
    rating,
    networkRating,
    whitelabelRating,
  } = getDataForTarget(target)

  const whitelabel = useWhitelabel()
  const networkLogo = whitelabel.getAsset(AssetName.LOGO_SMALL)

  const coverImageUrl = determineImage(
    target._type === TargetType.School,
    getIdFor(target),
    whitelabel,
    imageUrl,
  )

  return (
    <CalloutLayout>
      <PhotoContainer>
        <Link to={pagePath} target="_blank">
          {coverImageUrl && <Photo url={`${coverImageUrl}/`} mapView />}
          {displayedUpcomingEvent && renderUpcomingEvent(displayedUpcomingEvent)}
        </Link>
      </PhotoContainer>
      <InfoContainer>
        <Link to={pagePath} target="_blank" css={{ minHeight: '100%' }}>
          <NameContainer>
            {isVerified && renderVerifiedIcon()}
            <Strong preferredColor={whitelabel.listingNameColor(target)}>{name}</Strong>
          </NameContainer>
          {philosophy && <PhilosophyTag philosophy={philosophy} />}
          <SubInfoContainer>
            <div css={{ marginRight: `${spacing.majorScale(1)}px` }}>
              {pricing && (
                <Pricing
                  min={pricing.min}
                  max={pricing.max}
                  weeklyMin={pricing.weeklyMin}
                  weeklyMax={pricing.weeklyMax}
                />
              )}
            </div>
          </SubInfoContainer>
          <SubInfoContainer>
            {hours && <Hours start={hours.start} end={hours.end} />}
          </SubInfoContainer>
          {whitelabel.showByDefaultExceptIfFalse(
            AssetName.WONDERSCHOOL_RATING_DISPLAY__SEARCH__CALLOUT,
          ) &&
            rating &&
            rating.overall && (
              <RatingDisplay value={rating.overall} size={13} count={rating.count} />
            )}

          {whitelabel.hideByDefaultExceptIfTrue(
            AssetName.NETWORK_RATING_DISPLAY__SEARCH__CALLOUT,
          ) && <NetworkRating networkRating={networkRating} size={13} />}

          {whitelabel.hideByDefaultExceptIfTrue(AssetName.NETWORK_RATING_FILTERS_V2) && (
            <NetworkRatingV2 whitelabelRating={whitelabelRating} />
          )}

          {leadStatus && (
            <ActionLinks
              lead={leadStatus.lead}
              onRequestOpenScheduleTourModal={leadStatus.onRequestOpenScheduleTourModal}
              onRequestOpenCancelTourRequestModal={leadStatus.onRequestOpenCancelTourRequestModal}
            />
          )}
        </Link>
      </InfoContainer>
      {bookmarkButtonSchoolId && (
        <BookmarkContainer>
          <BookmarkButton schoolId={bookmarkButtonSchoolId} style={BookmarkButtonStyle.Dark} />
        </BookmarkContainer>
      )}
    </CalloutLayout>
  )

  function renderVerifiedIcon() {
    if (networkLogo) return <NetworkLogo src={networkLogo} size={12} />

    return <WSVerifiedIcon />
  }
}

function renderUpcomingEvent(upcomingEvent: UpcomingEvent) {
  const badge = <UpcomingEventBadge upcomingEvent={upcomingEvent} mapView />

  switch (upcomingEvent.__type) {
    case UpcomingEventType.Gathering:
      return <UpcomingGatheringEventContainer>{badge}</UpcomingGatheringEventContainer>
    case UpcomingEventType.Tour:
      return <UpcomingTourEventContainer>{badge}</UpcomingTourEventContainer>
    default:
      throw new UnreachableCaseError(upcomingEvent)
  }
}

Content.defaultProps = {
  upcomingEvent: null,
}
